// src/pages/common-components/Brand/BrandSection.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrands } from '../../Contexts/BrandContext';

/* ================= ANIMATION STYLES ================= */
const brandAnimStyles = `
  .bs-card {
    opacity: 0;
    transform: translateY(32px);
    transition:
      opacity 0.65s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.65s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    z-index: 1;
    isolation: isolate;
  }
  .bs-card.bs-card--visible {
    opacity: 1;
    transform: translateY(0);
  }

  .bs-card:nth-child(1) { transition-delay: 0ms; }
  .bs-card:nth-child(2) { transition-delay: 120ms; }
  .bs-card:nth-child(3) { transition-delay: 240ms; }
  .bs-card:nth-child(4) { transition-delay: 360ms; }
  .bs-card:nth-child(5) { transition-delay: 480ms; }
  .bs-card:nth-child(6) { transition-delay: 600ms; }

  .bs-card .bs-img-zoom {
    transition: transform 0.75s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .bs-card:hover .bs-img-zoom img {
    transform: scale(1.06);
    transition: transform 0.75s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .bs-card .block1 {
    overflow: hidden;
    isolation: isolate;
  }
  .bs-card .block1-txt {
    overflow: hidden;
  }

  .bs-card .block1-link {
    position: relative;
    display: inline-block;
  }
  .bs-card .block1-link::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: #fff;
    transition: width 0.3s ease 0.1s;
  }
  .bs-card .block1-txt:hover .block1-link::after {
    width: 100%;
  }

  .bs-card .block1-name {
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
                color 0.3s ease !important;
  }
  .bs-card .block1-txt:hover .block1-name {
    transform: translateY(-3px);
  }

  @media (max-width: 768px) {
    .bs-card {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
    .bs-card .block1-txt.respon3 {
      padding-left: 16px !important;
      padding-right: 16px !important;
      padding-top: 16px !important;
      padding-bottom: 16px !important;
    }
    .bs-card .block1-name {
      font-size: 16px !important;
    }
    .bs-card .stext-107 {
      font-size: 11px !important;
    }
    .bs-card .block1-link {
      font-size: 11px !important;
    }
  }
`;

/* ================= IMAGE WITH PLACEHOLDER ================= */
interface BrandImageProps {
  src?: string;
  alt: string;
}

const BrandImageWithPlaceholder: React.FC<BrandImageProps> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const imageSrc = src || 'asset/images/default-brand.jpg';

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '66.67%',
        overflow: 'hidden',
        backgroundColor: '#f8f9fa',
      }}
    >
      {!loaded && (
        <img
          src="asset/images/product-placeholder-landscape.png"
          alt="placeholder"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <img
        src={imageSrc}
        alt={alt}
        className="bs-img-zoom"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: loaded ? 'block' : 'none',
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
};

/* ================= BRAND SECTION ================= */
const BrandSection: React.FC = () => {
  const navigate = useNavigate();
  const { featuredBrands, loading, error } = useBrands();
  const rowRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile and update on resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Intersection Observer — desktop only
  useEffect(() => {
    if (!rowRef.current || window.innerWidth <= 768) return;
    const cards = rowRef.current.querySelectorAll<HTMLElement>('.bs-card');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('bs-card--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [featuredBrands]);

  if (!loading && (error || featuredBrands.length === 0)) return null;

  if (loading) {
    return (
      <div className="sec-banner bg0 p-t-80 p-b-50">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading brands...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleBrandClick = (brandId: string) => {
    navigate(`/shop?brand=${encodeURIComponent(brandId)}`);
  };

  // Applied directly as inline style on mobile — beats any CSS specificity
  const mobileColStyle: React.CSSProperties = {
    width: '50%',
    flex: '0 0 50%',
    maxWidth: '50%',
    paddingLeft: '8px',
    paddingRight: '8px',
  };

  return (
    <>
      <style>{brandAnimStyles}</style>

      <div className="sec-banner bg0 p-t-50 p-b-50">
        <div className="container">
          <div className="row" ref={rowRef}>
            {featuredBrands.map((brand) => (
              <div
                key={brand.id}
                className="col-sm-6 col-md-6 col-xl-4 p-b-30 bs-card"
                style={isMobile ? mobileColStyle : undefined}
              >
                <div className="block1 wrap-pic-w">
                  <BrandImageWithPlaceholder src={brand.imageUrl} alt={brand.name} />

                  <a
                    href={`/shop?brand=${encodeURIComponent(brand.id!)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleBrandClick(brand.id!);
                    }}
                    className="block1-txt ab-t-l s-full flex-col-l-sb p-lr-38 p-tb-34 trans-03 respon3"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="block1-txt-child1 flex-col-l">
                      <span className="block1-name ltext-102 trans-04 p-b-8">
                        {brand.name}
                      </span>
                      {brand.description && (
                        <span className="stext-107 cl2 trans-04">
                          {brand.description}
                        </span>
                      )}
                    </div>

                    <div className="block1-txt-child2 p-b-4 trans-05">
                      <div className="block1-link stext-101 cl0 trans-09">
                        Shop Brand
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default BrandSection;